<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";

// Оглавление статьи (jump-to). Якоря (id) проставляются на сервере в заголовки h2/h3,
// сюда приходит готовый список { id, text, level }.
//
// ⚠ СВЁРНУТО ДО ПАРЫ ПУНКТОВ, НО ВСЁ В HTML. По умолчанию (и на ПК, и на мобиле)
// видны первые PREVIEW пунктов, остальные скрыты v-show — то есть style="display:none",
// а не v-if. Разметка всех пунктов уходит в SSR-HTML: поисковик видит полный список
// ссылок-якорей и индексирует его. v-if здесь нельзя — скрытые пункты исчезли бы из HTML.
//
// Стартовое состояние одинаково на сервере и в браузере (свёрнуто) — без hydration mismatch.
const props = defineProps({
  items: { type: Array, default: () => [] },
  // inline — блок в начале статьи (свёрнут); rail — колонка справа на широком экране:
  // липкая, развёрнута целиком, подсвечивает раздел, который сейчас читают
  variant: { type: String, default: "inline" },
});
const isRail = computed(() => props.variant === "rail");

const PREVIEW = 2;
const activeId = ref("");
const expanded = ref(false);
const hiddenCount = computed(() => Math.max(0, props.items.length - PREVIEW));
let observer = null;

function go(id) {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
  activeId.value = id;
  history.replaceState(null, "", "#" + id);
}

onMounted(() => {
  // Скроллспай — подсвечиваем раздел, ближайший к верху вьюпорта
  const els = props.items.map((i) => document.getElementById(i.id)).filter(Boolean);
  if (!els.length) return;

  observer = new IntersectionObserver(
    (entries) => {
      const visible = entries
        .filter((e) => e.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visible[0]) activeId.value = visible[0].target.id;
    },
    { rootMargin: "-80px 0px -65% 0px", threshold: 0 }
  );
  els.forEach((el) => observer.observe(el));
});

onUnmounted(() => observer && observer.disconnect());
</script>

<template>
  <nav v-if="isRail && items.length >= 3" aria-label="Содержание статьи" class="text-[13px]">
    <div class="mb-2 px-3 text-xs font-bold uppercase tracking-wide text-gray-400">Содержание</div>
    <ul class="list-none m-0 p-0 border-l border-gray-200 max-h-[calc(100vh-10rem)] overflow-y-auto overscroll-contain">
      <li v-for="item in items" :key="item.id">
        <a :href="'#' + item.id" @click.prevent="go(item.id)"
          class="-ml-px block border-l-2 py-1.5 pr-2 leading-snug transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 rounded-sm"
          :class="[
            item.level === 3 ? 'pl-6 text-xs' : 'pl-3',
            activeId === item.id
              ? 'border-indigo-500 text-indigo-800 font-semibold'
              : 'border-transparent text-gray-500 hover:text-gray-900 hover:border-gray-300',
          ]">
          {{ item.text }}
        </a>
      </li>
    </ul>
  </nav>

  <nav v-else-if="items.length >= 3" id="toc" aria-label="Содержание статьи"
    class="my-6 rounded-xl border border-indigo-100 bg-indigo-50/50 overflow-hidden scroll-mt-20">
    <div class="flex items-center gap-2 px-4 pt-3 pb-1 text-sm font-bold text-indigo-900">
      <svg class="w-4 h-4 shrink-0 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h7" />
      </svg>
      Содержание
    </div>

    <ul class="px-2 pt-0 space-y-0.5 list-none m-0" :class="hiddenCount ? 'pb-1' : 'pb-3'">
      <li v-for="(item, i) in items" :key="item.id" v-show="expanded || i < PREVIEW"
        :class="item.level === 3 ? 'ml-4' : ''">
        <a :href="'#' + item.id" @click.prevent="go(item.id)"
          class="block rounded-lg px-3 py-1.5 text-sm leading-snug transition-colors"
          :class="activeId === item.id
            ? 'text-indigo-800 font-semibold underline underline-offset-4 decoration-2 decoration-indigo-400'
            : 'text-gray-600 hover:text-indigo-700 hover:bg-white/60'">
          {{ item.text }}
        </a>
      </li>
    </ul>

    <button v-if="hiddenCount" type="button" @click="expanded = !expanded" :aria-expanded="expanded"
      class="w-full flex items-center gap-1.5 px-5 pb-3 pt-1 text-left text-sm font-medium text-indigo-600 hover:text-indigo-800">
      {{ expanded ? "Свернуть" : `Показать всё содержание (ещё ${hiddenCount})` }}
      <svg class="w-4 h-4 shrink-0 transition-transform" :class="{ 'rotate-180': expanded }"
        fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>
  </nav>
</template>
